/*
 * Purpose: Mission map flow orientation — reorient/tidy without resetting the map
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import type { Node, Workflow } from '@/stores/workflowStore';
import { MAP_NODE_HEIGHT, MAP_NODE_WIDTH } from '@/lib/mapVisuals';

export type MapFlowOrientation = 'horizontal' | 'vertical' | 'auto';
export type ResolvedMapFlowOrientation = 'horizontal' | 'vertical';
export type MapFlowFormatMode = 'initial' | 'reorient' | 'tidy';

/** Narrow canvas → vertical flow reads better on mobile and stacked panels */
export const MAP_FLOW_VERTICAL_WIDTH_MAX = 720;
/** Portrait-ish viewport (height ≥ width × ratio) favors vertical */
export const MAP_FLOW_VERTICAL_ASPECT_MIN = 0.92;

export function resolveMapFlowOrientation(
  preference: MapFlowOrientation | undefined,
  viewportWidth: number,
  viewportHeight: number
): ResolvedMapFlowOrientation {
  if (preference === 'horizontal') return 'horizontal';
  if (preference === 'vertical') return 'vertical';

  const safeW = Math.max(viewportWidth, 1);
  const safeH = Math.max(viewportHeight, 1);
  const aspect = safeH / safeW;

  if (safeW <= MAP_FLOW_VERTICAL_WIDTH_MAX || aspect >= MAP_FLOW_VERTICAL_ASPECT_MIN) {
    return 'vertical';
  }
  return 'horizontal';
}

const H_GAP = 220;
const V_GAP = 150;
const X_START = 80;
const Y_START = 80;
const PARALLEL_GAP_H = MAP_NODE_HEIGHT + 48;
const PARALLEL_GAP_V = MAP_NODE_WIDTH + 48;

/** Infer current layout axis from how nodes are spread on the canvas. */
export function inferLayoutOrientation(nodes: Node[]): ResolvedMapFlowOrientation {
  if (nodes.length < 2) return 'horizontal';
  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const xSpan = Math.max(...xs) - Math.min(...xs);
  const ySpan = Math.max(...ys) - Math.min(...ys);
  return ySpan > xSpan * 1.05 ? 'vertical' : 'horizontal';
}

/** True when nodes share one point — needs first-time layout only. */
export function nodesNeedInitialLayout(nodes: Node[]): boolean {
  if (nodes.length === 0) return false;
  const keys = new Set(nodes.map((n) => `${Math.round(n.x)}:${Math.round(n.y)}`));
  return keys.size <= 1;
}

function anchorLayoutOrigin(nodes: Node[]): Node[] {
  if (nodes.length === 0) return nodes;
  const minX = Math.min(...nodes.map((n) => n.x));
  const minY = Math.min(...nodes.map((n) => n.y));
  return nodes.map((n) => ({
    ...n,
    x: n.x - minX + X_START,
    y: n.y - minY + Y_START,
  }));
}

/** Rotate layout 90° — flow axis becomes cross axis and vice versa. */
export function reorientWorkflowNodes(
  nodes: Node[],
  from: ResolvedMapFlowOrientation,
  to: ResolvedMapFlowOrientation
): Node[] {
  if (from === to || nodes.length === 0) return nodes.map((n) => ({ ...n }));

  const minX = Math.min(...nodes.map((n) => n.x));
  const minY = Math.min(...nodes.map((n) => n.y));

  return nodes.map((node) => {
    const x = node.x - minX;
    const y = node.y - minY;
    return { ...node, x: y, y: x };
  });
}

function computeFlowRanks(workflow: Pick<Workflow, 'nodes' | 'edges'>): Map<string, number> {
  const { nodes, edges } = workflow;
  const outEdges = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  for (const node of nodes) {
    outEdges.set(node.id, []);
    inDegree.set(node.id, 0);
  }

  for (const edge of edges) {
    outEdges.get(edge.from)?.push(edge.to);
    inDegree.set(edge.to, (inDegree.get(edge.to) ?? 0) + 1);
  }

  const startId =
    nodes.find((n) => n.type === 'start')?.id ??
    nodes.find((n) => (inDegree.get(n.id) ?? 0) === 0)?.id ??
    nodes[0]?.id;

  const rank = new Map<string, number>();
  if (!startId) return rank;

  const queue: string[] = [startId];
  rank.set(startId, 0);

  while (queue.length > 0) {
    const id = queue.shift()!;
    const r = rank.get(id) ?? 0;
    for (const next of outEdges.get(id) ?? []) {
      const nextRank = r + 1;
      const prev = rank.get(next);
      if (prev === undefined || nextRank > prev) {
        rank.set(next, nextRank);
        if (prev === undefined) queue.push(next);
      }
    }
  }

  for (const node of nodes) {
    if (!rank.has(node.id)) rank.set(node.id, 0);
  }

  return rank;
}

/** Snap to grid along flow axis — preserves parallel order from current positions. */
export function tidyWorkflowNodesPreserve(
  workflow: Pick<Workflow, 'nodes' | 'edges'>,
  orientation: ResolvedMapFlowOrientation
): Node[] {
  const { nodes } = workflow;
  if (nodes.length === 0) return [];

  const rank = computeFlowRanks(workflow);
  const crossKey = orientation === 'vertical' ? 'x' : 'y';
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  const byRank = new Map<number, string[]>();
  for (const [id, r] of rank) {
    if (!byRank.has(r)) byRank.set(r, []);
    byRank.get(r)!.push(id);
  }

  const sortedRanks = [...byRank.keys()].sort((a, b) => a - b);

  return sortedRanks.flatMap((r) => {
    const ids = [...(byRank.get(r) ?? [])].sort((a, b) => {
      const na = nodeById.get(a)!;
      const nb = nodeById.get(b)!;
      return na[crossKey] - nb[crossKey];
    });

    return ids.map((id, idx) => {
      const node = nodeById.get(id)!;
      if (orientation === 'vertical') {
        return {
          ...node,
          x: X_START + idx * PARALLEL_GAP_V,
          y: Y_START + r * V_GAP,
        };
      }
      return {
        ...node,
        x: X_START + r * H_GAP,
        y: Y_START + idx * PARALLEL_GAP_H,
      };
    });
  });
}

/** First-time topological layout (new / stacked nodes only). */
export function layoutWorkflowNodes(
  workflow: Pick<Workflow, 'nodes' | 'edges'>,
  orientation: ResolvedMapFlowOrientation
): Node[] {
  return tidyWorkflowNodesPreserve(workflow, orientation);
}

/**
 * Non-destructive map format:
 * - initial: grid layout only when nodes are stacked / unset
 * - reorient: rotate layout when axis changes (preserves relative map)
 * - tidy: snap to grid along flow axis without reordering parallel branches
 */
export function formatMapFlowLayout(
  workflow: Pick<Workflow, 'nodes' | 'edges'>,
  targetOrientation: ResolvedMapFlowOrientation,
  options: { mode?: MapFlowFormatMode; fromOrientation?: ResolvedMapFlowOrientation } = {}
): Node[] {
  const { nodes } = workflow;
  if (nodes.length === 0) return [];

  const mode = options.mode ?? 'tidy';

  if (mode === 'initial' || nodesNeedInitialLayout(nodes)) {
    return layoutWorkflowNodes(workflow, targetOrientation);
  }

  if (mode === 'reorient') {
    const from = options.fromOrientation ?? inferLayoutOrientation(nodes);
    if (from === targetOrientation) {
      return anchorLayoutOrigin(tidyWorkflowNodesPreserve(workflow, targetOrientation));
    }
    const rotated = reorientWorkflowNodes(nodes, from, targetOrientation);
    return anchorLayoutOrigin(rotated);
  }

  return tidyWorkflowNodesPreserve(workflow, targetOrientation);
}
